import { useCallback, useEffect, useRef, useState } from 'react';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListStatusRequest, ListStatusType } from '../../../../services/soap/project/requests/ListStatusRequest';
import { Flex, View } from '@adobe/react-spectrum';
import { enableRipple } from '@syncfusion/ej2-base';
import { ContextMenuComponent, MenuItemModel } from '@syncfusion/ej2-react-navigations';
import { AtollonIcon, MoreIcon, MassmailGreen, MassmailRed, TwitterIcon, FacebookIcon, LinkdinIcon, ClientIconBlue } from '../../Icons/IconsLib';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useTranslation } from 'react-i18next';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { ToastQueue } from '@react-spectrum/toast';
import { UpdateFolderRequest } from '../../../../services/soap/project/requests/UpdateFolderRequest';
import { CheckWorkFlowQueueRequest } from '../../../../services/soap/features/requests/CheckWorkFlowQueueRequest';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import styles from './HeaderToolbarComponent.module.css';

interface IFolderComponentMobileProps {
  headerData: any;
  statusColor: (color: string) => string;
  menuItems: MenuItemModel[];

  handleMenuItemClick?: (args: any) => void;
  onAIClicked?: () => void;
  selectedItem?: FPAData;
}
enableRipple(true);
const FolderComponentMobile = ({ headerData, statusColor, menuItems, handleMenuItemClick, onAIClicked, selectedItem }: IFolderComponentMobileProps) => {
  const { store, projectService, featureService } = useDependency();
  const [folderStatusList, setFolderStatusList] = useState<any>([]);
  const [fullPath, setFullPath] = useState<string>('');
  const [folderData, setFolderData] = useState<any>([]);
  const contextMenuRef = useRef<any>(null);
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      headerData && (await getFullPath());
      await getFolderData();
      await getFolderStatusArray();
    })();
  }, []);

  useEffect(() => {
    // Add the click event listener to the target element
    const targetElement = document.querySelector('.context-target');
    if (targetElement) {
      targetElement.addEventListener('click', handleContextMenu);
    }

    // Cleanup
    return () => {
      if (targetElement) {
        targetElement.removeEventListener('click', handleContextMenu);
      }
    };
  }, []);

  const getFolderStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listFolderStatus2(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Folder));

    if (res.result === 'OK' && res.STATUS.length > 0) {
      res?.STATUS.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color, TYPE } = status;
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color, types: TYPE });
      });

      var filtered_status = statusArray.filter((status: any) => status.types.find((type: any) => type.id === headerData?.folderType));
      setFolderStatusList(filtered_status);
    }
    return statusArray;
  }, []);

  const getAddress = () => {
    return headerData?.CONTACT?.COMPANY?.street !== undefined && headerData?.CONTACT?.COMPANY?.street !== null
      ? headerData?.CONTACT?.COMPANY?.street + ' ' + headerData?.CONTACT?.COMPANY?.city + ' ' + headerData?.CONTACT?.COMPANY?.zip + ' ' + headerData?.CONTACT?.COMPANY?.country
      : '';
  };

  const getFullPath = async () => {
    const response = await projectService.getContextPath(new GetContextPathRequest(store.Server, store.SessionId, headerData?.id || 0));
    setFullPath(response.LIST.map(item => item.CONTEXT.name).join(' > '));
  };

  function itemTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View UNSAFE_className={styles.dot} UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}></View>
        <View marginTop={5}>{data.name}</View>
      </Flex>
    );
  }
  function valueTemplate(data: any): JSX.Element {
    return (
      <Flex UNSAFE_className={styles.dot_text_container}>
        <View UNSAFE_className={styles.dot} UNSAFE_style={{ backgroundColor: `${statusColor(data.color)}` }}></View>
        <View marginTop={5} UNSAFE_style={{ marginLeft: '10px' }}>
          <span title={data?.name}>{data?.name && data?.name.length > 23 ? data?.name.substring(0, 23) + '...' : data?.name}</span>
        </View>
      </Flex>
    );
  }

  const handleContextMenu = (event: any) => {
    // Prevent the default context menu
    event.preventDefault();

    // Open the custom context menu at the click position
    if (contextMenuRef.current) {
      contextMenuRef.current.open(event.pageY, event.pageX);
    }
  };

  const handleAIClicked = () => {
    //console.log('handleAIClicked');
    if (onAIClicked) {
      onAIClicked();
    }
  };

  async function getFolderData() {
    if (selectedItem) {
      let { FOLDER } = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, selectedItem.id));
      setFolderData(FOLDER);
    }
  }

  async function updateFolderStatus(e: any) {
    let resFolder = await projectService.updateFolder(new UpdateFolderRequest(store.Server, store.SessionId, { ...folderData, customState: e.value }));
    if (resFolder.result == 'OK') {
      await featureService.checkWorkFlowQueue(new CheckWorkFlowQueueRequest(store.Server, store.SessionId));
      ToastQueue.positive(t('status_updated_successfully', { ns: 'layout_components' }));
    } else {
      ToastQueue.negative(resFolder.EXCEPTION?.message, { timeout: 1000 });
    }
  }

  return (
    <Flex UNSAFE_className={styles.header}>
      <View UNSAFE_className={styles.header_main}>
        <Flex direction="row" width={'100%'} justifyContent={'space-between'}>
          <View>
            <DropDownListComponent
              width={'250px'}
              dataSource={folderStatusList}
              fields={{ text: 'name', value: 'id' }}
              showClearButton={true}
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              value={headerData?.customState}
              onChange={(e: any) => updateFolderStatus(e)}
            />
          </View>
          <Flex direction={'row'}>
            <View>
              <div onClick={() => handleAIClicked()}>
                <AtollonIcon size={32} />
              </div>
            </View>
            <View>
              <ContextMenuComponent target=".context-target" id="contextmenu" ref={contextMenuRef} items={menuItems} select={handleMenuItemClick} />
              <span className="context-target" role="button" tabIndex={0}>
                <MoreIcon size={32} />
              </span>
            </View>
          </Flex>
        </Flex>
        <View UNSAFE_className={styles.header_child} marginTop="15px">
          <View UNSAFE_className={styles.frame_parent}>
            <Flex direction={'row'} justifyContent={'space-between'} width={'100%'}>
              <View UNSAFE_style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                <View>
                  <ClientIconBlue size={32} />
                </View>
                <View>
                  <b title={headerData?.name} className={styles.activity_name}>
                    {headerData?.name.length > 30 ? headerData?.name.substring(0, 30) + '...' : headerData?.name}
                  </b>
                </View>
              </View>
              <View>
                {headerData?.CONTACT?.COMPANY?.adsAllowed === 'N' ? (
                  <View UNSAFE_className={styles.ad_not_allowed}>
                    <MassmailRed size={20} />
                  </View>
                ) : (
                  <View UNSAFE_className={styles.ad_allowed}>
                    <MassmailGreen size={20} />
                  </View>
                )}
              </View>
            </Flex>
            <View UNSAFE_className={styles.folder_name_parent}>
              <View UNSAFE_className={styles.folder_name}>{fullPath}</View>
            </View>
            <View UNSAFE_className={styles.tag_parent} marginTop={'5px'}>
              <Flex direction={'row'} justifyContent={'space-between'} width={'100%'}>
                <View UNSAFE_className={styles.tag}>
                  <View UNSAFE_className={styles.activity_type}>
                    <View UNSAFE_className={styles.activity_type_text}>{headerData?.folderTypeName}</View>
                    {headerData?.referenceId ? (
                      <>
                        <View UNSAFE_className={styles.divider}></View> <View UNSAFE_className={styles.ref_id}>{headerData?.referenceId}</View>
                      </>
                    ) : (
                      <View UNSAFE_className={styles.divider_transparent}></View>
                    )}
                  </View>
                </View>
                <View UNSAFE_className={styles.socials_icons}>
                  <View UNSAFE_className={styles.icon_twitter}>
                    <TwitterIcon size={24} />
                  </View>

                  <View UNSAFE_className={styles.icon_facebook}>
                    <FacebookIcon size={24} />
                  </View>

                  <View UNSAFE_className={styles.icon_linkedin}>
                    <LinkdinIcon size={24} />
                  </View>
                </View>
              </Flex>
            </View>
          </View>
        </View>
        <Flex direction={'row'} justifyContent={'space-between'} width={'100%'} marginTop={10}>
          {headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber !== undefined ? (
            <View UNSAFE_className={styles.io_parent}>
              <View UNSAFE_className={styles.io_label}>IČO</View>
              <View UNSAFE_className={styles.io_text}>{headerData?.CONTACT?.COMPANY?.COMPANYINFO.regNumber}</View>{' '}
            </View>
          ) : (
            ''
          )}

          {headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber !== undefined ? (
            <View UNSAFE_className={styles.di_parent} marginX={15}>
              <View UNSAFE_className={styles.di_label}>DIČ</View>
              <View UNSAFE_className={styles.di_text}>{headerData?.CONTACT?.COMPANY?.COMPANYINFO.VATNumber}</View>
            </View>
          ) : (
            ''
          )}
          <View width={230}>
            {getAddress() !== '' ? (
              <>
                <View UNSAFE_className={styles.address_child}>{t('address', { ns: 'layout_components' })}</View>
                <View UNSAFE_className={styles.address}>
                  {getAddress()}
                  {/* <span title={getAddress()}>{getAddress().length > 25 ? getAddress().substring(0, 25) + '...' : getAddress()}</span> */}
                </View>
              </>
            ) : (
              ''
            )}
          </View>
        </Flex>
      </View>
    </Flex>
  );
};

export default FolderComponentMobile;
